.hero {
  width: min(1120px, 100%);
  margin: 0 auto;
  padding: 5rem 1.5rem 2.75rem;
}

.container {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
  gap: clamp(3rem, 7vw, 5rem);
  align-items: center;
}

.copy { min-width: 0; }

.eyebrow {
  margin: 0 0 1.15rem;
  color: var(--zen-teal-strong);
  font: 700 0.75rem/1.2 ui-monospace, 'SFMono-Regular', Menlo, monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.title {
  max-width: 760px;
  margin: 0;
  font-size: clamp(2.75rem, 7vw, 4.75rem);
  font-weight: 760;
  line-height: 0.98;
  letter-spacing: -0.055em;
  color: var(--zen-text-primary);

  span { display: block; }
  span + span { margin-top: 0.18em; }
}

.titleAccent {
  color: var(--zen-teal);
}

.subtitle {
  max-width: 710px;
  margin: 1.65rem 0 0;
  font-size: 1.125rem;
  line-height: 1.72;
  color: var(--zen-text-secondary);
}

.tagline {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 1.35rem 0 0;
  font-size: 0.82rem;
  color: var(--zen-text-muted);

  span { display: flex; gap: 0.55rem; }
  strong { min-width: 4.6rem; color: var(--zen-text-secondary); }
}

.cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem;
  margin-top: 1.85rem;
}

.ctaPrimary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 42px;
  border-radius: 10px;
  background: var(--zen-teal);
  padding: 0 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #062e2a;
  text-decoration: none;
  transition: all 0.15s;
  box-shadow: 0 8px 20px color-mix(in srgb, var(--zen-teal) 22%, transparent);
}

.ctaPrimary:hover {
  box-shadow: 0 2px 8px rgba(94, 234, 212, 0.3);
  color: #062e2a;
  text-decoration: none;
}

.ctaSecondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid var(--zen-border);
  background: var(--zen-bg-surface);
  padding: 0 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--zen-text-primary);
  text-decoration: none;
  transition: all 0.15s;
}

.visual {
  position: relative;
  display: grid;
  place-items: center;
  aspect-ratio: 1;
}

.logoPlate {
  z-index: 2;
  display: grid;
  place-items: center;
  width: 42%;
  aspect-ratio: 1;
  border: 1px solid var(--zen-border);
  border-radius: 28%;
  background: var(--zen-bg-surface);
  box-shadow: 0 24px 60px color-mix(in srgb, var(--zen-teal) 16%, transparent);

  img { width: 64%; height: auto; }
}

.orbit {
  position: absolute;
  border: 1px solid var(--zen-border);
  border-radius: 50%;
}

.orbitOne {
  inset: 14%;
  border-top-color: var(--zen-teal);
  animation: spin 20s linear infinite;
}

.orbitTwo {
  inset: 2%;
  border-right-color: var(--zen-blue);
  animation: spin 32s linear infinite reverse;
}

.satelliteOne,
.satelliteTwo {
  position: absolute;
  z-index: 3;
  display: grid;
  place-items: center;
  min-width: 3rem;
  height: 1.8rem;
  border: 1px solid var(--zen-border);
  border-radius: 999px;
  background: var(--zen-bg-surface);
  color: var(--zen-text-muted);
  font: 700 0.65rem/1 ui-monospace, monospace;
  letter-spacing: 0.08em;
}

.satelliteOne { top: 12%; right: 8%; }
.satelliteTwo { bottom: 13%; left: 4%; }

@keyframes spin { to { transform: rotate(360deg); } }

.terminal {
  margin-top: 4rem;
  overflow: hidden;
  border: 1px solid var(--zen-border);
  border-radius: 14px;
  background: var(--zen-code);
  box-shadow: 0 22px 55px rgb(8 15 35 / 15%);
}

.terminalBar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  min-height: 42px;
  padding: 0 0.9rem;
  border-bottom: 1px solid rgb(255 255 255 / 9%);
  color: #7f8da5;
  font: 600 0.72rem/1 ui-monospace, monospace;

  > button {
    display: inline-flex;
    justify-self: end;
    align-items: center;
    gap: 0.4rem;
    border: 0;
    padding: 0.35rem;
    background: transparent;
    color: #94a3b8;
    font: inherit;
    cursor: pointer;

    &:hover { color: #e2e8f0; }
  }
}

.terminalDots {
  display: flex;
  gap: 0.35rem;

  i { width: 7px; height: 7px; border-radius: 50%; background: #354054; }
}

.terminalBody {
  display: flex;
  gap: 0.75rem;
  padding: 1.45rem 1.6rem;
  overflow-x: auto;
  color: #dce5f5;
  font: 0.82rem/1.75 ui-monospace, 'SFMono-Regular', Menlo, monospace;

  code { white-space: nowrap; }
}

.prompt { color: #5eead4; }

@media (max-width: 780px) {
  .hero { padding: 3.5rem 1.25rem 2.25rem; }
  .container { grid-template-columns: 1fr; }
  .visual { display: none; }
  .terminal { margin-top: 3rem; }
  .terminalBar { grid-template-columns: 1fr auto; }
  .terminalBar > span:nth-child(2) { display: none; }
}

@media (max-width: 480px) {
  .title { font-size: clamp(2.65rem, 14vw, 3.6rem); }
  .subtitle { font-size: 1rem; }
  .terminalBody { padding: 1.15rem 1.25rem; }
}

@media (prefers-reduced-motion: reduce) {
  .orbit { animation: none; }
}

.ctaSecondary:hover {
  border-color: var(--zen-border-strong);
  background: var(--zen-bg-elevated);
  color: var(--zen-text-primary);
  text-decoration: none;
}
